import type { QueryClient } from '@tanstack/react-query';

const CLIENT_ANALYTICS_ROOTS = new Set([
  'analytics-stats',
  'analytics-stat-job',
  'analytics-stat-job-heartbeat',
  'analytics-stat-run-post',
  'analytics-stat-download-blob',
]);

const STAFF_ACCOUNT_ANALYTICS = new Set([
  'stats-access',
  'stat-column-catalog',
  'stat-limit',
  'stat-access-limit',
  'client-report-templates',
]);

export type InvalidateAnalyticsQueriesForAccountParams = {
  accountType: 'client' | 'partner';
  accountId: string;
  parentAccountId?: string | null;
};

function keyIncludesAccount(
  queryKey: readonly unknown[],
  accountId: string,
  accountType: 'client' | 'partner',
): boolean {
  const numericId = Number(accountId);
  const hasAccountId =
    queryKey.includes(accountId) || (Number.isFinite(numericId) && queryKey.includes(numericId));
  return hasAccountId && queryKey.includes(accountType);
}

function isAccountAnalyticsQuery(
  queryKey: readonly unknown[],
  params: InvalidateAnalyticsQueriesForAccountParams,
): boolean {
  const { accountType, accountId, parentAccountId } = params;
  const [root, second] = queryKey;

  if (typeof root === 'string' && CLIENT_ANALYTICS_ROOTS.has(root)) {
    return keyIncludesAccount(queryKey, accountId, accountType);
  }

  if (root === 'staff' && typeof second === 'string' && STAFF_ACCOUNT_ANALYTICS.has(second)) {
    return keyIncludesAccount(queryKey, accountId, accountType);
  }

  if (root === 'parent-child' && second === 'report-templates') {
    if (String(queryKey[3]) !== String(accountId)) {
      return false;
    }
    if (parentAccountId) {
      return String(queryKey[2]) === String(parentAccountId);
    }
    return true;
  }

  return false;
}

/**
 * Column access changes what stats/columns an account can see, so cached analytics
 * for that account (discovery, metadata, runs, catalog, template assignability)
 * should refetch. Leaves global Ops library / staff metadata alone.
 */
export function invalidateAnalyticsQueriesForAccount(
  queryClient: QueryClient,
  params: InvalidateAnalyticsQueriesForAccountParams,
) {
  queryClient.invalidateQueries({
    predicate: (query) => isAccountAnalyticsQuery(query.queryKey, params),
  });
}
